// src/components/webinar_funnel/sections/TableSummary.jsx
import React, { useMemo } from 'react';

const safeGet = (obj, path, def = 0) => {
  try {
    return path.split('.').reduce((cur, k) => cur?.[k], obj) ?? def;
  } catch {
    return def;
  }
};

const SUMMARY_COLORS = [
  { from: '#152451', to: '#1e3a6e' },  // Campaigns - darkblue
  { from: '#152451', to: '#0093cf' },  // Invites - darkblue→brightblue
  { from: '#92278f', to: '#b040ad' },  // Registrations - violet
  { from: '#009391', to: '#00b8b5' },  // Attendees - teal
  { from: '#f58025', to: '#f99b4e' },  // Bookings - brand
  { from: '#ed145b', to: '#f44c82' },  // Sales - pink
  { from: '#009391', to: '#24f4eb' },  // Revenue - teal→bright
];

const TableSummary = ({ data }) => {
  const totals = useMemo(() => {
    if (!data || !Array.isArray(data) || data.length === 0) return null;

    const t = data.reduce(
      (acc, row) => ({
        campaigns:     acc.campaigns     + 1,
        invitations:   acc.invitations   + safeGet(row, 'invitations'),
        registrations: acc.registrations + safeGet(row, 'registrations.total'),
        attendees:     acc.attendees     + safeGet(row, 'attendees.total'),
        bookings:      acc.bookings      + safeGet(row, 'bookings.total'),
        sales:         acc.sales         + safeGet(row, 'sales.total'),
        revenue:       acc.revenue       + safeGet(row, 'sales.revenue'),
      }),
      { campaigns:0, invitations:0, registrations:0, attendees:0, bookings:0, sales:0, revenue:0 },
    );

    const fmtMoney = (n) =>
      n >= 1_000_000 ? `$${(n / 1_000_000).toFixed(2)}M`
      : n >= 1_000   ? `$${(n / 1_000).toFixed(0)}K`
      : `$${n}`;

    return [
      { label: 'Campaigns',     value: t.campaigns },
      { label: 'Invites',       value: t.invitations.toLocaleString() },
      { label: 'Registrations', value: t.registrations.toLocaleString() },
      { label: 'Attendees',     value: t.attendees.toLocaleString() },
      { label: 'Bookings',      value: t.bookings.toLocaleString() },
      { label: 'Sales',         value: t.sales.toLocaleString() },
      { label: 'Revenue',       value: fmtMoney(t.revenue) },
    ];
  }, [data]);

  if (!totals) return null;

  return (
    <div className="grid grid-cols-4 sm:grid-cols-7 gap-2 px-4 py-3 md:px-6 bg-grey-50 border-b border-grey-200">
      {totals.map(({ label, value }, i) => {
        const color = SUMMARY_COLORS[i] || SUMMARY_COLORS[0];
        return (
          <div
            key={label}
            className="relative rounded-lg px-3 py-2.5 text-center overflow-hidden"
            style={{ background: `linear-gradient(135deg, ${color.from}, ${color.to})` }}
          >
            {/* Shine */}
            <div className="absolute inset-0 pointer-events-none" style={{
              background: 'linear-gradient(180deg, rgba(255,255,255,0.12) 0%, rgba(255,255,255,0) 50%)',
            }} />
            <div className="relative z-10">
              <div className="text-[10px] font-semibold uppercase tracking-wide text-white/70 mb-0.5 leading-tight">
                {label}
              </div>
              <div className="text-sm font-bold text-white leading-none">
                {value}
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default TableSummary;